import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, MapPin, Phone } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useLegalModal } from "@/components/LegalModal";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_ADDR = { street: "", zip: "", city: "", country: "CH", country_other: "" };

const INITIAL = {
  interest: "",
  first_name: "",
  last_name: "",
  company: "",
  email: "",
  phone: "",
  ca: { ...EMPTY_ADDR },
  event_location_status: "",
  venue: "",
  ea: { ...EMPTY_ADDR },
  expected_region: "",
  event_type: "",
  date_mode: "",
  start_date: "",
  end_date: "",
  flexible_period: "",
  participants: "",
  expected_participants: "",
  message: "",
};

const INTEREST_VALUES = ["events", "branding", "general"];
const EVENT_TYPE_VALUES = ["corporate", "club", "private", "public", "school", "other"];
const COUNTRY_VALUES = ["CH", "DE", "AT", "other"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls = (err) =>
  `w-full rounded-xl border bg-white px-4 py-3 text-neutral-900 placeholder:text-neutral-400 focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/30 focus:outline-none transition-colors ${
    err ? "border-red-400" : "border-slate-200"
  }`;

const ContactForm = () => {
  const { t, lang } = useLanguage();
  const c = t.contact;
  const { openPrivacy, Modal } = useLegalModal();
  const [data, setData] = useState(INITIAL);
  const [errors, setErrors] = useState({}); // fieldId -> error KEY (re-localized at render)
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };
  const setAddr = (which, k, v) => {
    setData((d) => ({ ...d, [which]: { ...d[which], [k]: v } }));
    const id = `${which}.${k}`;
    setErrors((er) => (er[id] ? { ...er, [id]: undefined } : er));
  };

  // Switching inquiry type clears no-longer-applicable conditional values
  const setInterest = (v) => {
    setData((d) => ({
      ...d,
      interest: v,
      event_location_status: "",
      venue: "",
      ea: { ...EMPTY_ADDR },
      expected_region: "",
      event_type: "",
      date_mode: "",
      start_date: "",
      end_date: "",
      flexible_period: "",
      participants: "",
      expected_participants: "",
    }));
    setErrors({});
  };

  const setLocStatus = (v) => {
    setData((d) => ({
      ...d,
      event_location_status: v,
      ...(v !== "different" ? { venue: "", ea: { ...EMPTY_ADDR } } : {}),
      ...(v !== "unknown" ? { expected_region: "" } : {}),
    }));
    setErrors({});
  };

  const setDateMode = (v) => {
    setData((d) => ({
      ...d,
      date_mode: v,
      ...(v !== "fixed" ? { start_date: "", end_date: "" } : {}),
      ...(v !== "flexible" ? { flexible_period: "" } : {}),
    }));
    setErrors({});
  };

  const isEventLike = data.interest === "events" || data.interest === "branding";
  const isGeneral = data.interest === "general";

  // Dynamic consecutive section numbering
  const sectionNumbers = {
    inquiry: 1,
    details: 2,
    address: 3,
    eventLocation: isEventLike ? 4 : null,
    additional: isEventLike ? 5 : 4,
  };

  const validate = () => {
    const e = {};
    const req = (id, cond) => { if (cond) e[id] = "required"; };
    req("interest", !data.interest);
    req("first_name", !data.first_name.trim());
    req("last_name", !data.last_name.trim());
    if (!data.email.trim()) e.email = "required";
    else if (!EMAIL_RE.test(data.email)) e.email = "emailInvalid";
    req("phone", !data.phone.trim());

    const addrCheck = (a, prefix, required) => {
      if (!required) return;
      req(`${prefix}.street`, !a.street.trim());
      req(`${prefix}.zip`, !a.zip.trim());
      req(`${prefix}.city`, !a.city.trim());
      req(`${prefix}.country`, !a.country);
      if (a.country === "other") req(`${prefix}.country_other`, !a.country_other.trim());
    };
    addrCheck(data.ca, "ca", !isGeneral);

    if (isEventLike) {
      req("event_location_status", !data.event_location_status);
      if (data.event_location_status === "different") addrCheck(data.ea, "ea", true);
      req("date_mode", !data.date_mode);
      if (data.date_mode === "fixed") {
        req("start_date", !data.start_date);
        if (data.start_date && data.end_date && data.end_date < data.start_date)
          e.end_date = "endBeforeStart";
      }
      if (data.date_mode === "flexible") req("flexible_period", !data.flexible_period.trim());
    }
    if (data.interest === "events") {
      req("event_type", !data.event_type);
      const n = parseInt(data.participants, 10);
      if (!data.participants.trim()) e.participants = "required";
      else if (!Number.isInteger(n) || n < 1) e.participants = "participantsInvalid";
    }
    if (data.interest === "branding" && data.expected_participants.trim()) {
      const n = parseInt(data.expected_participants, 10);
      if (!Number.isInteger(n) || n < 1) e.expected_participants = "participantsInvalid";
    }
    return e;
  };

  const buildPayload = () => {
    const addr = (a, required) => {
      const has = a.street.trim() || a.zip.trim() || a.city.trim();
      if (!required && !has) return null;
      return {
        street: a.street.trim() || null,
        zip: a.zip.trim() || null,
        city: a.city.trim() || null,
        country: a.country || null,
        country_other: a.country === "other" ? a.country_other.trim() || null : null,
      };
    };
    const p = {
      interest: data.interest,
      first_name: data.first_name.trim(),
      last_name: data.last_name.trim(),
      company: data.company.trim() || null,
      email: data.email.trim(),
      phone: data.phone.trim(),
      contact_address: addr(data.ca, !isGeneral),
      event_location_status: isEventLike ? data.event_location_status : "not_applicable",
      language: lang,
      message: data.message.trim() || null,
    };
    if (isEventLike) {
      p.venue = data.event_location_status === "different" ? data.venue.trim() || null : null;
      p.event_address = data.event_location_status === "different" ? addr(data.ea, true) : null;
      p.expected_region = data.event_location_status === "unknown" ? data.expected_region.trim() || null : null;
      p.date_mode = data.date_mode;
      if (data.date_mode === "fixed") {
        p.start_date = data.start_date || null;
        p.end_date = data.end_date || null;
      } else if (data.date_mode === "flexible") {
        p.flexible_period = data.flexible_period.trim();
      }
    }
    if (data.interest === "events") {
      p.event_type = data.event_type;
      p.participants = parseInt(data.participants, 10);
    } else if (data.interest === "branding") {
      p.participants = data.expected_participants.trim()
        ? parseInt(data.expected_participants, 10)
        : null;
    }
    return p;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      const first = document.querySelector('[data-error="true"]');
      first?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/quotes`, buildPayload());
      if (res.status >= 200 && res.status < 300) {
        toast.success(c.success);
        setData(INITIAL);
        setErrors({});
      } else {
        toast.error(c.error);
      }
    } catch (err) {
      toast.error(c.error);
    } finally {
      setSubmitting(false);
    }
  };

  const errMsg = (id) =>
    errors[id] ? (
      <p className="mt-1.5 text-xs text-red-500" data-testid={`err-${id}`}>
        {c.errors[errors[id]]}
      </p>
    ) : null;

  const Label = ({ htmlFor, children, required }) => (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-neutral-700 mb-1.5">
      {children} {required && <span className="text-[#FF7A00]">*</span>}
    </label>
  );

  const Radio = ({ name, value, checked, onChange, children, testid }) => (
    <label className="flex items-center gap-3 py-2 cursor-pointer" data-testid={testid}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="w-[18px] h-[18px] accent-[#0055FF]"
      />
      <span className="text-neutral-800">{children}</span>
    </label>
  );

  const SectionTitle = ({ n, children, first }) => (
    <h3
      className={`font-heading text-lg md:text-xl font-bold text-neutral-900 flex items-baseline justify-between ${
        first ? "" : "border-t border-slate-200 mt-8 pt-7"
      }`}
    >
      <span>{n}. {children}</span>
      {first && <span className="text-xs font-normal text-neutral-400">{c.requiredNote}</span>}
    </h3>
  );

  const AddressFields = ({ which, required, prefix }) => {
    const a = data[which];
    return (
      <div className="space-y-4">
        <div data-error={!!errors[`${prefix}.street`] || undefined}>
          <Label htmlFor={`${prefix}-street`} required={required}>{c.street}</Label>
          <input id={`${prefix}-street`} data-testid={`${prefix}-street`} value={a.street}
            onChange={(e) => setAddr(which, "street", e.target.value)}
            className={inputCls(errors[`${prefix}.street`])} placeholder={lang === "de" ? "Musterstrasse 12" : lang === "fr" ? "Rue de l'Exemple 12" : "Via Esempio 12"} />
          {errMsg(`${prefix}.street`)}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div data-error={!!errors[`${prefix}.zip`] || undefined}>
            <Label htmlFor={`${prefix}-zip`} required={required}>{c.zip}</Label>
            <input id={`${prefix}-zip`} data-testid={`${prefix}-zip`} value={a.zip}
              onChange={(e) => setAddr(which, "zip", e.target.value)}
              className={inputCls(errors[`${prefix}.zip`])} placeholder="8000" inputMode="text" />
            {errMsg(`${prefix}.zip`)}
          </div>
          <div data-error={!!errors[`${prefix}.city`] || undefined}>
            <Label htmlFor={`${prefix}-city`} required={required}>{c.city}</Label>
            <input id={`${prefix}-city`} data-testid={`${prefix}-city`} value={a.city}
              onChange={(e) => setAddr(which, "city", e.target.value)}
              className={inputCls(errors[`${prefix}.city`])} placeholder={lang === "it" ? "Zurigo" : "Zürich"} />
            {errMsg(`${prefix}.city`)}
          </div>
        </div>
        <div data-error={!!errors[`${prefix}.country`] || undefined}>
          <Label htmlFor={`${prefix}-country`} required={required}>{c.country}</Label>
          <select id={`${prefix}-country`} data-testid={`${prefix}-country`} value={a.country}
            onChange={(e) => setAddr(which, "country", e.target.value)}
            className={inputCls(errors[`${prefix}.country`])}>
            {COUNTRY_VALUES.map((v) => (
              <option key={v} value={v}>{c.countries[v]}</option>
            ))}
          </select>
          {errMsg(`${prefix}.country`)}
        </div>
        {a.country === "other" && (
          <div data-error={!!errors[`${prefix}.country_other`] || undefined}>
            <Label htmlFor={`${prefix}-country-other`} required={required}>{c.countryOther}</Label>
            <input id={`${prefix}-country-other`} data-testid={`${prefix}-country-other`} value={a.country_other}
              onChange={(e) => setAddr(which, "country_other", e.target.value)}
              className={inputCls(errors[`${prefix}.country_other`])} />
            {errMsg(`${prefix}.country_other`)}
          </div>
        )}
      </div>
    );
  };

  const DateFields = ({ labelKey }) => (
    <div className="space-y-4">
      <div data-error={!!errors.date_mode || undefined}>
        <span className="block text-sm font-medium text-neutral-700 mb-1.5">
          {c[labelKey]} <span className="text-[#FF7A00]">*</span>
        </span>
        <div className="flex flex-wrap gap-x-6">
          <Radio name="date_mode" value="fixed" checked={data.date_mode === "fixed"} onChange={setDateMode} testid="datemode-fixed">{c.fixed}</Radio>
          <Radio name="date_mode" value="flexible" checked={data.date_mode === "flexible"} onChange={setDateMode} testid="datemode-flexible">{c.flexible}</Radio>
        </div>
        {errMsg("date_mode")}
      </div>

      {data.date_mode === "fixed" && (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div data-error={!!errors.start_date || undefined}>
              <Label htmlFor="start-date" required>{c.startDate}</Label>
              <input id="start-date" data-testid="start-date" type="date" value={data.start_date}
                onChange={(e) => set("start_date", e.target.value)}
                className={inputCls(errors.start_date)} />
              {errMsg("start_date")}
            </div>
            <div data-error={!!errors.end_date || undefined}>
              <Label htmlFor="end-date">{c.endDate}</Label>
              <input id="end-date" data-testid="end-date" type="date" value={data.end_date}
                min={data.start_date || undefined}
                onChange={(e) => set("end_date", e.target.value)}
                className={inputCls(errors.end_date)} />
              {errMsg("end_date")}
            </div>
          </div>
          <p className="text-xs text-neutral-500">{c.oneDayHint}</p>
        </div>
      )}

      {data.date_mode === "flexible" && (
        <div data-error={!!errors.flexible_period || undefined}>
          <Label htmlFor="flexible-period" required>{c.flexiblePeriod}</Label>
          <input id="flexible-period" data-testid="flexible-period" value={data.flexible_period}
            onChange={(e) => set("flexible_period", e.target.value)}
            className={inputCls(errors.flexible_period)} placeholder={c.flexiblePlaceholder} />
          {errMsg("flexible_period")}
        </div>
      )}
    </div>
  );

  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 md:py-28 bg-white scroll-mt-24">
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0055FF]">{c.kicker}</div>
          <h2 className="mt-3 font-heading text-3xl md:text-5xl font-bold tracking-tight text-neutral-900">{c.title}</h2>
          <p className="mt-4 text-lg text-neutral-600 leading-relaxed">{c.subtitle}</p>

          <ul className="mt-10 space-y-6">
            <li className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-2xl bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{c.emailLabel}</div>
                <a href="mailto:info@footpoolevents.ch" className="font-semibold text-neutral-900 hover:text-[#0055FF]">
                  info@footpoolevents.ch
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-2xl bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{c.phoneLabel}</div>
                <a
                  href="tel:+41762769395"
                  data-testid="contact-phone-link"
                  className="font-semibold text-neutral-900 hover:text-[#0055FF]"
                >
                  +41 76 276 93 95
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-2xl bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center">
                <MapPin className="w-5 h-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{c.locationLabel}</div>
                <div data-testid="contact-location" className="font-semibold text-neutral-900">{c.locationValue}</div>
              </div>
            </li>
          </ul>

          <div data-testid="contact-address" className="mt-10 text-sm leading-relaxed text-neutral-600">
            <div className="font-semibold text-neutral-900">footpoolevents.ch</div>
            <div>Winggel 13</div>
            <div>9468 Sax</div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          noValidate
          className="ph-no-capture lg:col-span-7 rounded-[32px] bg-[#F8FAFC] border border-slate-200/70 p-7 md:p-10"
        >
          <p className="text-neutral-600 text-sm md:text-base leading-relaxed">{c.formIntro}</p>

          {/* 1. Inquiry */}
          <div className="mt-6">
            <SectionTitle n={sectionNumbers.inquiry} first>{c.sections.inquiry}</SectionTitle>
            <div className="mt-4" data-error={!!errors.interest || undefined}>
              <span className="block text-sm font-medium text-neutral-700 mb-1">
                {c.interestLabel} <span className="text-[#FF7A00]">*</span>
              </span>
              {INTEREST_VALUES.map((v) => (
                <Radio key={v} name="interest" value={v} checked={data.interest === v}
                  onChange={setInterest} testid={`interest-${v}`}>
                  {c.interests[v]}
                </Radio>
              ))}
              {errMsg("interest")}
            </div>
          </div>

          {/* 2. Contact details */}
          <div>
            <SectionTitle n={sectionNumbers.details}>{c.sections.details}</SectionTitle>
            <div className="mt-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div data-error={!!errors.first_name || undefined}>
                  <Label htmlFor="first-name" required>{c.firstName}</Label>
                  <input id="first-name" data-testid="contact-first-name" value={data.first_name}
                    onChange={(e) => set("first_name", e.target.value)}
                    className={inputCls(errors.first_name)} placeholder={c.firstName} autoComplete="given-name" />
                  {errMsg("first_name")}
                </div>
                <div data-error={!!errors.last_name || undefined}>
                  <Label htmlFor="last-name" required>{c.lastName}</Label>
                  <input id="last-name" data-testid="contact-last-name" value={data.last_name}
                    onChange={(e) => set("last_name", e.target.value)}
                    className={inputCls(errors.last_name)} placeholder={c.lastName} autoComplete="family-name" />
                  {errMsg("last_name")}
                </div>
              </div>
              <div>
                <Label htmlFor="company">{c.company}</Label>
                <input id="company" data-testid="contact-company" value={data.company}
                  onChange={(e) => set("company", e.target.value)}
                  className={inputCls(false)} placeholder={c.optional} autoComplete="organization" />
              </div>
              <div data-error={!!errors.email || undefined}>
                <Label htmlFor="email" required>{c.email}</Label>
                <input id="email" data-testid="contact-email" type="email" value={data.email}
                  onChange={(e) => set("email", e.target.value)}
                  className={inputCls(errors.email)} placeholder={c.emailPlaceholder} autoComplete="email" />
                {errMsg("email")}
              </div>
              <div data-error={!!errors.phone || undefined}>
                <Label htmlFor="phone" required>{c.phone}</Label>
                <input id="phone" data-testid="contact-phone" type="tel" value={data.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className={inputCls(errors.phone)} placeholder={c.phonePlaceholder} autoComplete="tel" />
                {errMsg("phone")}
              </div>
            </div>
          </div>

          {/* 3. Contact address */}
          <div>
            <SectionTitle n={sectionNumbers.address}>{c.sections.address}</SectionTitle>
            <div className="mt-5">
              <AddressFields which="ca" required={!isGeneral} prefix="ca" />
            </div>
          </div>

          {/* 4. Event location (events/branding only) */}
          {isEventLike && (
            <div>
              <SectionTitle n={sectionNumbers.eventLocation}>{c.sections.eventLocation}</SectionTitle>
              <div className="mt-4" data-error={!!errors.event_location_status || undefined}>
                <Radio name="eventloc" value="same" checked={data.event_location_status === "same"} onChange={setLocStatus} testid="eventloc-same">
                  {c.eventLoc.same}
                </Radio>
                <Radio name="eventloc" value="different" checked={data.event_location_status === "different"} onChange={setLocStatus} testid="eventloc-different">
                  {c.eventLoc.different}
                </Radio>
                <Radio name="eventloc" value="unknown" checked={data.event_location_status === "unknown"} onChange={setLocStatus} testid="eventloc-unknown">
                  {c.eventLoc.unknown}
                </Radio>
                {errMsg("event_location_status")}
              </div>

              {data.event_location_status === "different" && (
                <div className="mt-4 space-y-4">
                  <div>
                    <Label htmlFor="venue">{c.venue}</Label>
                    <input id="venue" data-testid="event-venue" value={data.venue}
                      onChange={(e) => set("venue", e.target.value)}
                      className={inputCls(false)} placeholder={c.optional} />
                  </div>
                  <AddressFields which="ea" required prefix="ea" />
                </div>
              )}

              {data.event_location_status === "unknown" && (
                <div className="mt-4">
                  <Label htmlFor="expected-region">{c.expectedRegion}</Label>
                  <input id="expected-region" data-testid="expected-region" value={data.expected_region}
                    onChange={(e) => set("expected_region", e.target.value)}
                    className={inputCls(false)} placeholder={c.optional} />
                </div>
              )}
            </div>
          )}

          {/* Additional details */}
          <div>
            <SectionTitle n={sectionNumbers.additional}>{c.sections.additional}</SectionTitle>
            <div className="mt-5 space-y-4">
              {data.interest === "events" && (
                <div data-error={!!errors.event_type || undefined}>
                  <Label htmlFor="event-type" required>{c.eventType}</Label>
                  <select id="event-type" data-testid="event-type" value={data.event_type}
                    onChange={(e) => set("event_type", e.target.value)}
                    className={inputCls(errors.event_type)}>
                    <option value="">{c.selectPlaceholder}</option>
                    {EVENT_TYPE_VALUES.map((v) => (
                      <option key={v} value={v}>{c.eventTypes[v]}</option>
                    ))}
                  </select>
                  {errMsg("event_type")}
                </div>
              )}

              {isEventLike && <DateFields labelKey={data.interest === "branding" ? "usagePeriod" : "dateMode"} />}

              {data.interest === "events" && (
                <div data-error={!!errors.participants || undefined}>
                  <Label htmlFor="participants" required>{c.participants}</Label>
                  <input id="participants" data-testid="participants" type="number" min="1" step="1"
                    value={data.participants} onChange={(e) => set("participants", e.target.value)}
                    className={inputCls(errors.participants)} placeholder={c.participantsPlaceholder} inputMode="numeric" />
                  <p className="mt-1.5 text-xs text-neutral-500">{c.participantsHint}</p>
                  {errMsg("participants")}
                </div>
              )}

              {data.interest === "branding" && (
                <div data-error={!!errors.expected_participants || undefined}>
                  <Label htmlFor="expected-participants">{c.expectedParticipants}</Label>
                  <input id="expected-participants" data-testid="expected-participants" type="number" min="1" step="1"
                    value={data.expected_participants} onChange={(e) => set("expected_participants", e.target.value)}
                    className={inputCls(errors.expected_participants)} inputMode="numeric" />
                  {errMsg("expected_participants")}
                </div>
              )}

              <div>
                <Label htmlFor="message">{c.message}</Label>
                <textarea id="message" data-testid="contact-message" rows={4} value={data.message}
                  onChange={(e) => set("message", e.target.value)}
                  className={`${inputCls(false)} resize-none`} placeholder={c.messagePlaceholder} />
              </div>
            </div>
          </div>

          <p className="mt-6 text-xs text-neutral-500 leading-relaxed">
            {c.privacyNoticePre}{" "}
            <button type="button" onClick={openPrivacy} data-testid="contact-privacy-link"
              className="underline underline-offset-2 text-neutral-700 hover:text-[#0055FF] transition-colors">
              {c.privacyNoticeLink}
            </button>
          </p>

          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit"
            className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#0055FF] px-8 py-3.5 text-sm md:text-base font-semibold text-white hover:bg-[#0044dd] transition-all shadow-xl shadow-[#0055FF]/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? c.submitting : c.submit}
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
      <Modal />
    </section>
  );
};

export default ContactForm;
